import { Link } from '@tanstack/react-router';
import type { MargensOrcamentosResponse } from '@triade/contracts';
import { formatarContagem, formatarData, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { BarraMargem, EixoMargem } from './barra-margem';

const ROTULO_FAIXA = { green: 'na meta ou acima', orange: 'abaixo da meta', red: 'abaixo do mínimo' } as const;

/** Resumo do grafico para leitores de tela (so reaproveita o que a API devolveu). */
export function descricaoGrafico(r: MargensOrcamentosResponse): string {
  const primeiro = r.itens[0];
  const ultimo = r.itens[r.itens.length - 1];
  if (!primeiro || !ultimo) return 'Nenhum orçamento com lucro.';
  const abaixo = r.itens.filter((o) => o.faixa !== 'green').length;
  const parcial = r.total > r.itens.length ? ` (os ${formatarContagem(r.itens.length)} de menor lucro, de ${formatarContagem(r.total)})` : '';
  return `Lucro de ${formatarContagem(r.itens.length)} orçamentos${parcial} entre ${formatarFracaoPercentual(primeiro.exibicao.lucro)} e ${formatarFracaoPercentual(ultimo.exibicao.lucro)}; ${formatarContagem(abaixo)} abaixo da meta`;
}

/**
 * "Lucro por orcamento" (docs/design/Margens.dc.html): barras do menor para o maior lucro, com linhas de minimo e meta.
 * Larguras e posicoes vem prontas da API; ha uma tabela equivalente so para leitores de tela.
 */
export function GraficoOrcamentos({ dados }: { dados: MargensOrcamentosResponse }) {
  const { escala } = dados;
  return (
    <>
      <div role="img" aria-label={descricaoGrafico(dados)} data-testid="grafico-orcamentos" className="flex flex-col gap-2">
        {dados.itens.map((o) => (
          <BarraMargem
            key={o.id}
            rotulo={
              <Link to="/vender/orcamentos/$id" params={{ id: o.id }} aria-label={`Abrir orçamento ${o.numero}`} data-testid="barra-orcamento" className="text-ink no-underline hover:underline">
                #{o.numero}
              </Link>
            }
            lucro={o.exibicao.lucro}
            mcPct={o.exibicao.mcPct}
            barra={o.exibicao.barra}
            faixa={o.faixa}
            posicaoMinimo={o.exibicao.posicaoMinimo}
            posicaoMeta={o.exibicao.posicaoMeta}
            titulo={`${o.tabelaNome}${o.clienteNome ? ` · ${o.clienteNome}` : ''} · ${formatarMoeda(o.exibicao.receita)}`}
          />
        ))}
      </div>
      <EixoMargem maximo={escala.maximo} minimo={escala.minimo} meta={escala.meta} />
      <table className="sr-only">
        <caption>Lucro por orçamento, do menor para o maior</caption>
        <thead>
          <tr>
            <th scope="col">Orçamento</th>
            <th scope="col">Data</th>
            <th scope="col">Tabela</th>
            <th scope="col">Lucro</th>
            <th scope="col">Situação do lucro</th>
          </tr>
        </thead>
        <tbody>
          {dados.itens.map((o) => (
            <tr key={o.id}>
              <th scope="row">{o.numero}</th>
              <td>{formatarData(o.exibicao.data)}</td>
              <td>{o.tabelaNome}</td>
              <td>{formatarFracaoPercentual(o.exibicao.lucro)}</td>
              <td>{o.faixa ? ROTULO_FAIXA[o.faixa] : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
